// apps/roleforge-app/src/components/audit/AssetEditorPane.tsx
//
// Renders the full operating-model editor inside the middle column when an
// asset is opened from the right column. Adapted from AAB's
// CurrentAssetPanel + RichAssetEditor patterns: header strip with title and
// "back to chat" affordance, body fills the rest. The actual operating-model
// rendering still lives in OperatingModelPanel.

import { ArrowLeft } from 'lucide-react';
import { OperatingModelPanel } from '../canvas/OperatingModelPanel';
import type { AuditRow } from '../../lib/api-client';
import type { SseEventRouter } from '../../lib/sse-event-router';
import type { ShellMode } from './AuditPersistentShell';
import type { AssetId } from './AssetsColumn';
import type { OperatingModelState } from '../../lib/use-operating-model-state';

interface Props {
  assetId: AssetId;
  audit: AuditRow;
  router: SseEventRouter;
  mode: ShellMode;
  state: OperatingModelState;
  refreshAudit: () => Promise<void>;
  jwt?: string | null;
  onBackToChat: () => void;
  validationLocking?: boolean;
  validationLockError?: string | null;
  onValidationLockIn?: () => Promise<void>;
}

function titleFor(assetId: AssetId, state: OperatingModelState): string {
  if (assetId === 'operating-model') return 'AI Workforce Blueprint';
  if (assetId === 'opportunity-matrix') return 'Opportunity Matrix';
  if (assetId === 'roi-summary') return 'ROI Summary';
  if (assetId === 'skill-pack') return 'Skill Pack';
  if (assetId.startsWith('opportunity:')) {
    const oppId = assetId.slice('opportunity:'.length);
    const opp = state.opportunities.find((o) => o.opportunity_id === oppId);
    return opp?.title ?? 'Opportunity';
  }
  return 'Asset';
}

export function AssetEditorPane({
  assetId,
  audit,
  router,
  mode,
  state,
  refreshAudit,
  jwt,
  onBackToChat,
  validationLocking,
  validationLockError,
  onValidationLockIn,
}: Props) {
  const title = titleFor(assetId, state);

  return (
    <div
      data-testid="asset-editor-pane"
      data-asset-id={assetId}
      className="flex h-full flex-col"
    >
      <header className="flex items-center justify-between border-b border-line bg-card px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBackToChat}
            data-testid="asset-editor-back"
            className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-graphite hover:bg-slate-100 dark:bg-slate-800/60 hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to chat
          </button>
          <span className="h-4 w-px bg-line" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <OperatingModelPanel
          audit={audit}
          router={router}
          mode={mode}
          refreshAudit={refreshAudit}
          jwt={jwt}
          state={state}
          validationLocking={validationLocking}
          validationLockError={validationLockError}
          onValidationLockIn={onValidationLockIn}
        />
      </div>
    </div>
  );
}
